import { useCallback, useEffect, useId } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { create } from 'zustand';
import { backStep, fallbackTarget, isMenuState, parseTrail, recordEntry, showBack, type Trail } from './backNavigationModel';

/**
 * The TopBar "חזרה" button (owner, 2026-10-07: "a Back button that returns to the
 * page I was on before"; since round 2, 2026-10-11, on detail pages only, see
 * `isDetailPath`). Logic in `backNavigationModel`.
 *
 * - `useHistoryTrail()` runs once at the router root and writes down what each
 *   history index of this tab showed (sessionStorage, so a reload keeps it).
 * - `useBackNavigation()` gives the TopBar `visible` and `goBack`: `navigate(-n)`
 *   to the previous page in this tab, or "up" to the parent when there is none.
 *   Both are router navigations, so the leave guard (`useLeaveGuard` /
 *   `useBlocker`) asks "לצאת בלי לשמור?" on a dirty screen exactly as it does for
 *   the browser's own Back.
 * - `useBackParent(path)` lets a page name a better parent than its route's list
 *   (a project task → `/tasks`, a milestone → its project). Call it before any
 *   early return, with `null` while the data is loading.
 */

const STORAGE_KEY = 'ote_back_trail';

let memoryTrail: Trail | null = null;

function readTrail(): Trail {
  if (memoryTrail) return memoryTrail;
  try {
    memoryTrail = parseTrail(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    memoryTrail = {};
  }
  return memoryTrail;
}

function writeTrail(trail: Trail) {
  memoryTrail = trail;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    /* storage unavailable: the in-memory trail still serves this page load */
  }
}

/**
 * This entry's index, when the history state is the router's own for `key`. The
 * first entry of a tab has no key in its state (the router calls it "default").
 */
function historyIndex(key: string): number | null {
  const state = window.history.state as { idx?: unknown; key?: unknown } | null;
  if (!state || typeof state.idx !== 'number') return null;
  const sameEntry = state.key === key || (key === 'default' && state.key == null);
  return sameEntry ? state.idx : null;
}

/** Mount once under the router (App root): records every page this tab shows. */
export function useHistoryTrail() {
  const location = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    const idx = historyIndex(location.key);
    if (idx == null) return;
    writeTrail(recordEntry(readTrail(), idx, { pathname: location.pathname, search: location.search, menu: isMenuState(location.state) }, navigationType === 'PUSH'));
  }, [location, navigationType]);
}

const useBackParentStore = create<{ owner: string | null; to: string | null }>(() => ({ owner: null, to: null }));

/** The page's own parent for Back when this tab has no earlier page (see above). */
export function useBackParent(to: string | null) {
  const owner = useId();
  useEffect(() => {
    useBackParentStore.setState({ owner, to });
    return () => {
      if (useBackParentStore.getState().owner === owner) useBackParentStore.setState({ owner: null, to: null });
    };
  }, [owner, to]);
}

export function useBackNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const pageParent = useBackParentStore((s) => s.to);

  const goBack = useCallback(() => {
    const idx = historyIndex(location.key);
    const step = idx == null ? null : backStep(readTrail(), idx, location);
    if (step != null) navigate(step);
    else navigate(fallbackTarget(location.pathname, location.search, pageParent));
  }, [location, navigate, pageParent]);

  // Detail pages (round 2, 2026-10-11) and, since D-347 §2, any page reached from another
  // screen inside the app; a page opened from the menu shows none.
  const idx = historyIndex(location.key);
  const navigationType = useNavigationType();
  // A replace (or reload / back-forward) on an entry already recorded as a menu entry of this page keeps that mark;
  // a fresh push never inherits a stale entry at the same index.
  const stored = idx != null ? readTrail()[idx] : undefined;
  const keptMenu = navigationType !== 'PUSH' && !!stored?.menu && stored.pathname.replace(/\/+$/, '') === location.pathname.replace(/\/+$/, '');
  return { visible: showBack(readTrail(), idx, location, isMenuState(location.state) || keptMenu), goBack };
}
